import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { TdDialogService } from '@covalent/core/dialogs';
import { from, Observable, Subject, throwError } from 'rxjs';
import { catchError, map, switchMap, take, takeUntil } from 'rxjs/operators';
import { TcsConfigurationService } from 'src/app/core/configuration.service';
import { Logger } from 'src/app/shared/tcs-logging/logger';
import { AuthenticationService } from 'src/lib/tcs-identity/authentication.service';
import { firstValueOrUndefined } from 'src/util/rxjs';
import { createSafeProps } from '../tcs-app-insights/app-insights-util';

const alertLogout = async (
  tdDialog: TdDialogService,
  title: string,
  message: string,
  buttonText: string = 'Logout',
) => {
  const dialog = tdDialog.openAlert({
    title,
    disableClose: true,
    message,
    closeButton: buttonText,
  });
  return firstValueOrUndefined(dialog.afterClosed().pipe(take(1)));
};

@Injectable()
export class AuthenticationInterceptor implements HttpInterceptor, OnDestroy {
  private readonly _forbidden$ = new Subject<HttpErrorResponse>();
  private readonly _unauthorized$ = new Subject<HttpRequest<any>>();
  private _onDestroy$ = new Subject();
  private _tcsGraphQLUrl: string;
  constructor(
    private _auth: AuthenticationService,
    private _tdDialog: TdDialogService,
    private _logger: Logger,
    private _config: TcsConfigurationService,
    private _router: Router,
  ) {
    this._tcsGraphQLUrl = _config.value.tcs.graphqlUri;
    this._unauthorized$
      .pipe(
        switchMap((req) =>
          this._auth.isAuthenticated$.pipe(
            map((isAuthenticatedCheck) => ({ req, isAuthenticatedCheck })),
          ),
        ),
        takeUntil(this._onDestroy$),
      )
      .subscribe(async ({ req, isAuthenticatedCheck }) => {
        _logger.info('Detected unauthorized request', {
          url: req.url,
          isAuthenticatedCheck: String(isAuthenticatedCheck),
        });
        const isRenewed = await this._auth.tryRenewToken();
        if (!isRenewed) {
          await alertLogout(
            this._tdDialog,
            'Session Expired',
            'Please login again to continue.',
            'Login',
          );
          await this._auth.logout({
            caller: 'AuthenticationInterceptor._unauthorized$',
            url: window.location.href,
          });
        }
      });
    this._forbidden$.pipe(take(1), takeUntil(this._onDestroy$)).subscribe(async (errResponse) => {
      const idClaims = this._auth.idClaims;
      const accessClaims = this._auth.accessClaims;
      const username = accessClaims?.sub;
      const email = idClaims?.email;
      const errorMessage = errResponse.error?.message ?? errResponse.message;
      const userProps = createSafeProps({ email, username, accessClaims, idClaims });

      await this._router.navigate(['/unauthorized']);
      if (/access not enabled/i.test(errorMessage)) {
        this._logger.warning(
          `Access not enabled (email: ${email}, username: ${username})`,
          userProps,
        );
        await alertLogout(this._tdDialog, 'Access Not Enabled', errorMessage);
      } else {
        this._logger.warning(`User not found (email: ${email}, username: ${username})`, userProps);
        await alertLogout(
          this._tdDialog,
          'User Not Found',
          `Authentication successful, but no user with the email "${email}" and username "${username}" has been granted access to this application. If you have used another username or email to access this application before, try logging out and logging in with that username or email instead.`,
        );
      }
      this._auth.logout({ caller: 'AuthenticationInterceptor._forbidden$' });
    });
  }
  public intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return from(this._auth.getAccessToken()).pipe(
      switchMap((token) => {
        if (token != null) {
          req = req.clone({
            setHeaders: {
              Authorization: 'Bearer ' + token,
            },
          });
        }
        return next.handle(req);
      }),
      catchError((err) => {
        if (req.url.includes(this._tcsGraphQLUrl) && err instanceof HttpErrorResponse) {
          if (err.status === 401) {
            this._unauthorized$.next(req);
          } else if (err.status === 403) {
            this._forbidden$.next(err);
          } else if (err.status < 500) {
            this._logger.warning(
              `[AuthenticationInterceptor.catchError] Unknown graphQL error.`,
              createSafeProps(err),
            );
          }
        }
        return throwError(err);
      }),
    );
  }

  public ngOnDestroy(): void {
    this._onDestroy$.next(null);
    this._onDestroy$.complete();
  }
}
